import { type ChangeEvent, useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import classNames from 'classnames';

import {
    Breakpoint,
    LocationFilterWithRange,
    type TypeAheadSuggestion,
    useBreakpoint,
    ValidationStates,
} from '@jsmdg/yoshi';
import { type ProductSearchLocationItem } from '../../../shared/types';
import { useLocationFilter } from '../../hooks/useLocationFilter';
import { SearchReducerActionType } from '../../reducers/searchReducer';
import { DistanceBoundaryInKm } from '../../types/enums/distanceBoundaryInKm';
import { trackLocationResetClicked } from '../GiftBoxRelatedProducts/Filter.tracking';
import styles from './LocationFilterComponent.module.scss';

const messages = defineMessages({
    placeholderLong: { defaultMessage: 'Wo (PLZ oder Ort)' },
    placeholderShort: { defaultMessage: 'Wo?' },
    rangeMessage: { defaultMessage: 'Umkreis:' },
});

type LocationFilterComponentType = {
    readonly locationName?: string;
    readonly onSubmit: (type: SearchReducerActionType, value?: ProductSearchLocationItem) => void;
    readonly onReset: () => void;
    readonly storedValue: number | undefined;
    readonly onFilterSubmit: (sliderValue?: number) => void;
};

const LocationFilterComponent = ({
    locationName = '',
    onFilterSubmit,
    onReset,
    onSubmit,
    storedValue,
}: LocationFilterComponentType): JSX.Element => {
    const intl = useIntl();
    const [sessionToken, setSessionToken] = useState<null | string>(null);
    const [error, setError] = useState('');
    const [suggestionData, setSuggestionData] = useState<TypeAheadSuggestion[]>([]);
    const [selectedSuggestion, setSelectedSuggestion] = useState<TypeAheadSuggestion>({
        id: '',
        title: locationName || '',
        highlighted: false,
    });

    const isMobile = !useBreakpoint(Breakpoint.SM);

    const { getDetails, getSuggestions } = useLocationFilter({
        setSessionToken,
        sessionToken,
        onSubmit,
        setSuggestionData,
    });

    useEffect(() => {
        if (selectedSuggestion.title) {
            getSuggestions(selectedSuggestion.title)
                // eslint-disable-next-line promise/prefer-await-to-then
                .then(data => setSuggestionData(data))
                .catch(error_ => setError((error_ as Error).message));
        }
    }, [getSuggestions, selectedSuggestion.title]);

    const onSelect = (suggestion: TypeAheadSuggestion): void => {
        setSelectedSuggestion({
            ...suggestion,
            title: `${suggestion.title}${suggestion.subTitle ? `, ${suggestion.subTitle}` : ''}`,
        });
        if (suggestion.id && suggestion.title && sessionToken) {
            getDetails(suggestion.id, suggestion.title).catch(error_ =>
                setError((error_ as Error).message),
            );
        }
    };

    const onInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
        setSelectedSuggestion({
            ...selectedSuggestion,
            title: e.target.value,
        });
    };

    const onInputClear = async (): Promise<void> => {
        await trackLocationResetClicked('ResetLocation');
        onSubmit(SearchReducerActionType.LocationFilterReset);
        setSelectedSuggestion({
            ...selectedSuggestion,
            title: '',
        });
        onReset();
    };

    return (
        <div className={classNames('p-3x mb-2x', styles.dark)}>
            <LocationFilterWithRange
                placeholder={
                    isMobile
                        ? intl.formatMessage(messages.placeholderLong)
                        : intl.formatMessage(messages.placeholderShort)
                }
                rangeValue={storedValue || DistanceBoundaryInKm.DEFAULT}
                minRange={DistanceBoundaryInKm.MIN}
                maxRange={DistanceBoundaryInKm.MAX}
                onInputClear={onInputClear}
                onInputChange={onInputChange}
                selectedValue={selectedSuggestion}
                rangePanelMessage={intl.formatMessage(messages.rangeMessage)}
                onClickOutside={onFilterSubmit}
                onChangeRange={onFilterSubmit}
                onSelect={onSelect}
                validationState={error ? ValidationStates.Error : ValidationStates.Success}
                showHelperText={!!error}
                helperText={error}
                suggestionData={suggestionData}
                rangeHeaderClasses={styles.rangeHeader}
            />
        </div>
    );
};

export { LocationFilterComponent };
